// everything related to resizing, viewport, window, document, elements

// imports --------------------------------------------------------------------
import * as Vars from './0-vars.js';
import * as Once from './2-once.js';
import * as Page from './3-page.js';
import * as Scroll from './5-scroll.js';

// exported variables ---------------------------------------------------------
export let windowWidth = 0;
export let windowInnerWidth = 0;
export let windowHeight = 0;
export let windowInnerHeight = 0;
export let viewportHeight = 0;
export let fullTouch = 0;
export let navbarHeight = 0;
export let navbarPresent = true;
export let navbarCurrentHeight = 0;

export let documentWidth = 0;
export let documentHeight = 0;
export let maxBodyScrollTop = 0;
export let maxBodyScrollLeft = 0;

export let blockResizeObserver = false; // set to true during animations to not call setResize

export let headerHeight = 0;

// for hiding sticky
export let stickyHeight;
export let stickySelector = '.header';

export function setBlockResizeObserver(value) {
  blockResizeObserver = value;
}

export let introTextEl = null; 
export let introMEl = null; 
export let introEl = null; 

// local variables -------------------------------------------------------------
const rootStyle = document.documentElement.style;
const resizeHolders = ['.main', '.popover']; // comment to turn off resize observer

let prevInnerWidth = 0;
let prevObserverWidth = 0;
let prevObserverHeight = 0;

let footerTop;
let footerHeight;
let first = true;

let introTextHeight = 0; // used in intro section

let screenRatio = 1; // used for portrait nav

// resize listeners ------------------------------------------------------------
export const initResize = () => {
  // visual viewport resize listener
  window.visualViewport.addEventListener('resize', setResize);

  Vars.mqPortrait.addEventListener('change', () => {
    setTimeout(setResize, 100);
  });

  // resize observer
  if (typeof resizeHolders !== 'undefined') {
    for (let i = 0; i < resizeHolders.length; i++) {
      // add an observer for each holder that resizes
      const observedEls = document.querySelectorAll(resizeHolders[i]);
      observedEls.forEach((observedEl) => {
        if (observedEl !== null) new ResizeObserver(observerResize).observe(observedEl);
      });
    }
  }

  // run once even if body is fixed
  doSaveOffsets();
};

// resize function -------------------------------------------------------------
export const setResize = (force = false) => {
  if (typeof force === 'object') force = false;
  // console.log('setResize', force);

  // do resizing actions
  const ftEl = document.querySelector('.full-touch');
  if (!ftEl) return;
  fullTouch = ftEl.offsetHeight; // height including navbar on touch
  windowInnerHeight = window.innerHeight; // height minus current navbar on touch
  navbarCurrentHeight = fullTouch - windowInnerHeight; // current navbar on touch
  if (first) navbarHeight = navbarCurrentHeight; // navbar height
  first = false;
  windowHeight = fullTouch - navbarHeight; // height minus navbar on touch
  windowWidth = document.documentElement.clientWidth; // width without scrollbar
  windowInnerWidth = window.innerWidth; // width with scrollbar
  viewportHeight = window.visualViewport.height; // height minus current navbar and keyboard on touch
  navbarPresent = windowInnerHeight === windowHeight ? 1 : 0;
  screenRatio = windowInnerWidth / windowInnerHeight; // used for portrait nav

  rootStyle.setProperty('--window-width', windowWidth + 'px');
  rootStyle.setProperty('--window-height', windowHeight + 'px');
  rootStyle.setProperty('--inner-height', windowInnerHeight + 'px'); // sometimes wrong on android
  rootStyle.setProperty('--viewport-height', viewportHeight + 'px');
  rootStyle.setProperty('--navbar-height', navbarHeight + 'px');
  rootStyle.setProperty('--current-navbar-height', navbarCurrentHeight + 'px');
  rootStyle.setProperty('--screen-ratio', screenRatio + ''); // used for portrait nav

  // on touch, if the body is fixed, menu is always present
  // so adding height: 100% is the way to add a correct size popover
  // or by setting height to windowHeight with js

  if ('checkBlockScroll' in Scroll) Scroll.checkBlockScroll(); // before resizing

  // when resizing width or height ---------------------------------------------
  // resize things

  // save sizes for css, use Rect because of subpixel values!!
  const headerEl = document.querySelector('.header');
  if (headerEl) {
    headerHeight = headerEl.getBoundingClientRect().height;
    rootStyle.setProperty('--header-height', headerHeight + 'px');
  }

  const footerEl = document.querySelector('.footer');
  if (footerEl) {
    footerHeight = footerEl.getBoundingClientRect().height;
    rootStyle.setProperty('--footer-height', footerHeight + 'px');
  }

  const holder = Vars.mqPortrait.matches ? 'visible-portrait' : 'visible-landscape';
  introTextEl = document.querySelector(`.intro.${holder} .intro__text`);
  introEl = document.querySelector(`.intro.${holder}`); // used in scroll
  introMEl = document.querySelector(`.intro.${holder} .intro__m`); // used in scroll

  if (introTextEl) {
    introTextHeight = introTextEl.getBoundingClientRect().height;
    rootStyle.setProperty('--intro-text-height', introTextHeight + 'px');
  }

  const eventHeaderEls = document.querySelectorAll('.event__header');
  eventHeaderEls.forEach((eventHeaderEl) => {
    const parentEl = eventHeaderEl.parentElement;
    if (parentEl) {
      const height = eventHeaderEl.getBoundingClientRect().height;
      parentEl.style.setProperty('--event-header-height', height + 'px');

      const linkEl = parentEl.querySelector('.event__link');
      if (linkEl) {
        const width = linkEl.getBoundingClientRect().width;
        parentEl.style.setProperty('--event-link-width', width + 'px');
      }
    }
  });

  // hiding sticky
  const stickyEl = document.querySelector(stickySelector);
  if (stickyEl) {
    stickyHeight = stickyEl.getBoundingClientRect().height;
    rootStyle.setProperty('--sticky-height', stickyHeight + 'px');
  }

  // check if resize width or forced
  if (windowInnerWidth === prevInnerWidth && force === false) {
    saveOffsets();
    Scroll.setScroll();
    return;
  }

  prevInnerWidth = windowInnerWidth;

  // from here is skipped when only resizing height ----------------------------
  // code...

  // last
  saveOffsets();
  Scroll.setScroll();
};

// functions -------------------------------------------------------------------
export const saveOffsets = () => {
  // console.log('saveOffsets');

  if ('fixer' in Once && typeof Once.fixer?.bodyFixed !== 'undefined') {
    if (Once.fixer.bodyFixed !== true) {
      doSaveOffsets();
    }
  } else {
    doSaveOffsets();
  }
};

const doSaveOffsets = () => {
  // console.log('doSaveOffsets'); 
  // always call this through saveOffsets

  // save offsets here
  documentHeight = document.documentElement.scrollHeight; // entire document height
  maxBodyScrollTop = documentHeight - windowInnerHeight;
  rootStyle.setProperty('--document-height', documentHeight + 'px');

  documentWidth = document.documentElement.scrollWidth; // entire document width
  maxBodyScrollLeft = documentWidth - windowWidth;


  // set stagger per line
  if('lineHolderEls' in Page && Page.lineHolderEls) {
    Page.lineHolderEls.forEach(holderEl => {
      let line = 0; // higher than 0 is delay for the first line
      let prevTop = 0;
      const partsEls = holderEl.querySelectorAll('.part__text, .part__img');
      partsEls.forEach((partEl, index) => {
        const top = getCoords(partEl).top;
        if (top > prevTop) line++; // image might have a smaller top, so don't add a line then

        prevTop = top;
        // partEl.dataset.faderDelay = index * -80 + line * 200; // -80 to remove fader stagger if addStaggerToFaderDelay is true
        partEl.dataset.faderDelay = line * 200;
      });
    });
  }
  
  // documentHeight based calculations
  // code...
};

// helper functions ------------------------------------------------------------
// improved version of getBoundingClientRect ignoring zoom
const getCoords = (el) => { // crossbrowser version
  const rect = el.getBoundingClientRect();
  
  const body = document.body;
  const docEl = document.documentElement;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  const top  = rect.top +  scrollTop - clientTop;
  const left = rect.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
}
// check for resizing of holders
const observerResize = () => {
  // console.log('observerResize', blockResizeObserver, windowWidth, prevObserverWidth, windowHeight, prevObserverHeight);

  // prevent double resize when resizing window
  if (
    (windowWidth !== prevObserverWidth || windowHeight !== prevObserverHeight)
    && prevObserverWidth !== 0
  ) {
    prevObserverWidth = windowWidth;
    prevObserverHeight = windowHeight;
    return;
  }

  prevObserverWidth = windowWidth;
  prevObserverHeight = windowHeight;

  // not animating? do resize
  if (!blockResizeObserver) setResize(true);
};

